import { FaxesPage } from '@/pages/Faxes';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

// Unlike Practitioners there is no `search` term param — `Communication` has no
// searchable field for the payload attachment title, so a search box can't be
// wired server-side.
const FaxSearchSchema = z.object({
  direction: z.enum(['inbound', 'outbound']).default('inbound'),
  organizationId: z.string().min(1).optional(),
  // Persisted across tab switches; the page only applies it on the inbound tab.
  readStatus: z.enum(['read', 'unread']).optional(),
  // Defaults to the unassigned work queue; persisted across tab switches like readStatus.
  assignment: z.enum(['all', 'unassigned']).default('unassigned'),
  pageSize: z.number().int().min(1).max(1000).default(20),
  // The fax open in the viewer, by `Communication` id — so a fax can be linked to from outside the
  // dashboard (a task, a message) rather than only reached by clicking a row. The viewer reads the
  // fax by id on open, so a linked fax renders even when the filters behind it exclude it.
  fax: z.string().min(1).optional(),
});

const RouteComponent = (): React.ReactNode => {
  // oxlint-disable-next-line no-use-before-define
  const { direction, organizationId, readStatus, assignment, pageSize, fax } = Route.useSearch();
  // oxlint-disable-next-line no-use-before-define
  const navigate = useNavigate({ from: Route.fullPath });

  // Memoized where the other setters are not: the page holds this one in the dependencies of its
  // column definitions, so an identity that churned every render would rebuild them every render.
  //
  // Opening pushes, so Back closes the viewer rather than leaving the dashboard; closing replaces,
  // so Back does not then reopen what was just closed.
  const setFaxId = useCallback(
    (fax: string | undefined) => {
      void navigate({ search: (prev) => ({ ...prev, fax }), replace: !fax });
    },
    [navigate],
  );

  return (
    <FaxesPage
      direction={direction}
      setDirection={(direction) => {
        void navigate({ search: (prev) => ({ ...prev, direction }) });
      }}
      organizationId={organizationId}
      setOrganizationId={(organizationId) => {
        void navigate({ search: (prev) => ({ ...prev, organizationId }) });
      }}
      readStatus={readStatus}
      setReadStatus={(readStatus) => {
        void navigate({ search: (prev) => ({ ...prev, readStatus }) });
      }}
      assignment={assignment}
      setAssignment={(assignment) => {
        void navigate({ search: (prev) => ({ ...prev, assignment }) });
      }}
      pageSize={pageSize}
      setPageSize={(pageSize) => {
        void navigate({ search: (prev) => ({ ...prev, pageSize }) });
      }}
      faxId={fax}
      setFaxId={setFaxId}
    />
  );
};

export const Route = createFileRoute('/_auth/faxes/')({
  component: RouteComponent,
  loader: () => ({ crumb: 'Faxes' }),
  validateSearch: FaxSearchSchema,
});
